/**
 * Minimal loader for Plaid Link (drop-in for `react-plaid-link`, without adding
 * a dependency for a PoC). Injects the CDN script once, then opens the modal.
 */

const SCRIPT_SRC = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
const SCRIPT_ID = 'plaid-link-initialize';

type PlaidLinkHandler = { open: () => void; exit: () => void; destroy: () => void };

type PlaidLinkGlobal = {
  create: (config: {
    token: string;
    onSuccess: (publicToken: string, metadata: { institution?: { name?: string } }) => void;
    onExit: (error: { display_message?: string; error_message?: string } | null) => void;
  }) => PlaidLinkHandler;
};

declare global {
  interface Window {
    Plaid?: PlaidLinkGlobal;
  }
}

function loadScript(): Promise<PlaidLinkGlobal> {
  if (window.Plaid) return Promise.resolve(window.Plaid);

  return new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    const script =
      existing instanceof HTMLScriptElement ? existing : document.createElement('script');
    const onLoad = () => {
      if (window.Plaid) resolve(window.Plaid);
      else reject(new Error('Plaid Link script loaded without a global'));
    };
    script.addEventListener('load', onLoad);
    script.addEventListener('error', () => reject(new Error('Failed to load Plaid Link script')));
    if (!existing) {
      script.id = SCRIPT_ID;
      script.src = SCRIPT_SRC;
      script.async = true;
      document.head.appendChild(script);
    }
  });
}

/** Opens Plaid Link and resolves with the public token, or `null` if dismissed. */
export async function openPlaidLink(linkToken: string): Promise<string | null> {
  const plaid = await loadScript();

  return new Promise((resolve, reject) => {
    const handler = plaid.create({
      token: linkToken,
      onSuccess: (publicToken) => {
        handler.destroy();
        resolve(publicToken);
      },
      onExit: (error) => {
        handler.destroy();
        if (error)
          reject(new Error(error.display_message || error.error_message || 'Plaid Link failed'));
        else resolve(null);
      },
    });
    handler.open();
  });
}
